import { Camera, ClipboardList, Eye, HelpCircle, ShieldAlert } from 'lucide-react';
import {
  createViewerExtensionRegistry,
  normalizeLayoutPolygon,
  type HotspotObjectCreateProps,
  type InteractiveViewerExtension,
  type LayoutOverlayProps,
} from '@ironview/interactive-viewer';

import { ObservationHotspotForm } from '../panorama/ObservationHotspotForm';
import { PunchItemHotspotForm } from '../panorama/PunchItemHotspotForm';
import { RfiHotspotForm } from '../panorama/RfiHotspotForm';
import { SafetyHotspotForm } from '@/features/safety/SafetyHotspotForm';
import { getHeatMapColor, getHeatMapSoftColor } from '../utils/heatMap';

const numericId = (value: string | number): number | undefined => {
  const id = Number(value);
  return Number.isFinite(id) ? id : undefined;
};

const PunchItemCreateExtension = ({
  context,
  photoId,
  existingHotspotId,
  position,
  onCreated,
  onCancel,
}: HotspotObjectCreateProps) => (
  <PunchItemHotspotForm
    photoId={Number(photoId)}
    projectId={numericId(context.projectId)}
    viewPosition={position}
    hotspotId={
      existingHotspotId == null
        ? undefined
        : Number(existingHotspotId)
    }
    onSuccess={onCreated}
    onCancel={onCancel}
  />
);

const ObservationCreateExtension = ({
  context,
  photoId,
  existingHotspotId,
  position,
  onCreated,
  onCancel,
}: HotspotObjectCreateProps) => (
  <ObservationHotspotForm
    photoId={Number(photoId)}
    projectId={numericId(context.projectId)}
    viewPosition={position}
    hotspotId={
      existingHotspotId == null
        ? undefined
        : Number(existingHotspotId)
    }
    onSuccess={onCreated}
    onCancel={onCancel}
  />
);

const RfiCreateExtension = ({
  context,
  photoId,
  existingHotspotId,
  position,
  onCreated,
  onCancel,
}: HotspotObjectCreateProps) => (
  <RfiHotspotForm
    photoId={Number(photoId)}
    projectId={numericId(context.projectId)}
    viewPosition={position}
    hotspotId={
      existingHotspotId == null
        ? undefined
        : Number(existingHotspotId)
    }
    onSuccess={onCreated}
    onCancel={onCancel}
  />
);

const SafetyCreateExtension = ({
  context,
  photoId,
  existingHotspotId,
  position,
  onCreated,
  onCancel,
}: HotspotObjectCreateProps) => (
  <SafetyHotspotForm
    photoId={Number(photoId)}
    projectId={numericId(context.projectId)}
    viewPosition={position}
    hotspotId={existingHotspotId == null ? undefined : Number(existingHotspotId)}
    onSuccess={onCreated}
    onCancel={onCancel}
  />
);

interface IssueCounts {
  observations: number;
  punch_items: number;
  rfis: number;
  submittals: number;
  safety: number;
  total: number;
}

const emptyCounts = (): IssueCounts => ({
  observations: 0,
  punch_items: 0,
  rfis: 0,
  submittals: 0,
  safety: 0,
  total: 0,
});

const readIssueCounts = (
  metadata: Readonly<Record<string, unknown>> | undefined,
): IssueCounts => {
  const source =
    metadata?.openIssueCounts &&
    typeof metadata.openIssueCounts === 'object'
      ? (metadata.openIssueCounts as Record<string, unknown>)
      : {};
  const observations = Number(source.observations ?? 0);
  const punchItems = Number(source.punch_items ?? 0);
  const rfis = Number(source.rfis ?? 0);
  const submittals = Number(source.submittals ?? 0);
  const safety = Number(source.safety ?? 0);
  return {
    observations,
    punch_items: punchItems,
    rfis,
    submittals,
    safety,
    total: Number(
      source.total ??
        observations + punchItems + rfis + submittals + safety,
    ),
  };
};

export const Ironview360HeatMapLayer = ({
  areaTags,
  hotspots,
  viewport,
}: LayoutOverlayProps) => {
  const countsByPhoto = new Map<number, IssueCounts>();
  hotspots.forEach(({ hotspot }) => {
    const photoId = Number(hotspot.photoId);
    if (!Number.isFinite(photoId)) return;
    const current = countsByPhoto.get(photoId) ?? emptyCounts();
    const next = readIssueCounts(hotspot.metadata);
    countsByPhoto.set(photoId, {
      observations: current.observations + next.observations,
      punch_items: current.punch_items + next.punch_items,
      rfis: current.rfis + next.rfis,
      submittals: current.submittals + next.submittals,
      safety: current.safety + next.safety,
      total: current.total + next.total,
    });
  });

  return (
    <svg
      className="ironview360-heat-map-extension"
      width={viewport.width}
      height={viewport.height}
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
      }}
      aria-hidden
    >
      <g
        transform={`translate(${viewport.position.x}, ${viewport.position.y}) scale(${viewport.scale})`}
      >
        {areaTags.map((areaTag) => {
          const points = normalizeLayoutPolygon(areaTag.points);
          if (points.length < 3) return null;
          const issueCount = [...new Set(areaTag.photoIds.map(Number))]
            .filter(Number.isFinite)
            .reduce(
              (total, photoId) =>
                total + (countsByPhoto.get(photoId)?.total ?? 0),
              0,
            );
          const color = getHeatMapColor(issueCount);
          return (
            <polygon
              key={String(areaTag.id)}
              points={points.map(([x, y]) => `${x},${y}`).join(' ')}
              fill={getHeatMapSoftColor(issueCount)}
              stroke={color}
              strokeWidth={2}
              data-issue-count={issueCount}
            />
          );
        })}
      </g>
    </svg>
  );
};

export const ironview360ConstructionExtension: InteractiveViewerExtension = {
  id: 'ironview360-construction-objects',
  hotspotObjects: [
    {
      type: 'punch_item',
      label: 'Punch Item',
      description: 'Flag an issue that needs to be resolved',
      icon: ClipboardList,
      CreateComponent: PunchItemCreateExtension,
    },
    {
      type: 'quick_punch_item',
      label: 'Quick Punch Item',
      description: 'Take a photo and add a short description',
      icon: Camera,
    },
    {
      type: 'observation',
      label: 'Observation',
      description:
        'Document a safety, quality, or site condition finding',
      icon: Eye,
      CreateComponent: ObservationCreateExtension,
    },
    {
      type: 'rfi',
      label: 'RFI',
      description:
        'Raise a Request For Information for design/coordination',
      icon: HelpCircle,
      CreateComponent: RfiCreateExtension,
    },
    {
      type: 'safety',
      label: 'Safety Issue',
      description: 'Flag a safety hazard or unsafe site condition',
      icon: ShieldAlert,
      CreateComponent: SafetyCreateExtension,
    },
  ],
};

export const ironview360HeatMapExtension: InteractiveViewerExtension = {
  id: 'ironview360-issue-heat-map',
  layoutOverlays: [
    {
      id: 'issue-heat-map',
      Layer: Ironview360HeatMapLayer,
    },
  ],
};

export const ironview360ViewerExtensionRegistry =
  createViewerExtensionRegistry([
    ironview360ConstructionExtension,
    ironview360HeatMapExtension,
  ]);
